import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { EntitySelect } from '../../shared/components/EntitySelect';
import { GeoLocationMultiSelect } from '../../shared/components/GeoLocationMultiSelect';
import { TreeEntitySelect } from '../../shared/components/TreeEntitySelect';
import { StatusChip } from '../../shared/components/StatusChip';
import { resources } from '../../core/services/resources';
import type {
  AIModel,
  LineOfBizCategory,
  LineOfBizPersona,
  PromptAssignmentStatus,
  PromptFunnelStage,
  PromptImportance,
  TenantBrand,
} from '../../core/types/data-models';

const STATUSES: PromptAssignmentStatus[] = ['ACTIVE', 'DISABLED'];
const IMPORTANCES: PromptImportance[] = ['LOW', 'MEDIUM', 'HIGH', 'VERY_HIGH'];

/** Per-template assignment overrides — shared by Prompt and PromptSet attaches. */
export interface ReportTemplateAssignmentOverrides {
  status: PromptAssignmentStatus;
  /** Empty array = no geo override. */
  geoLocationIds: number[];
  /** Empty array = no AI model override. */
  aiModelIds: number[];
  categoryId: number | null;
  personaId: number | null;
  stageId: number | null;
  brandId: number | null;
  /**
   * Per-template importance override. Only meaningful when attaching a
   * Prompt — for PromptSet attach the dialog hides the field and the
   * mutation ignores this value.
   */
  importance: PromptImportance;
}

const EMPTY_OVERRIDES: ReportTemplateAssignmentOverrides = {
  status: 'ACTIVE',
  geoLocationIds: [],
  aiModelIds: [],
  categoryId: null,
  personaId: null,
  stageId: null,
  brandId: null,
  importance: 'MEDIUM',
};

interface ReportTemplateAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (overrides: ReportTemplateAssignmentOverrides) => Promise<void>;
  saving?: boolean;
  error?: string | null;
  onErrorClose?: () => void;
  /** Title shown at the top of the dialog. */
  title?: string;
  /**
   * Preview card rendered above the form fields — typically a small
   * read-only block showing the prompt text or prompt-set name so the
   * user remembers what they're configuring.
   */
  header?: ReactNode;
  /**
   * Initial override values. For Prompt attach this is seeded from the
   * prompt's `default*` fields; for PromptSet attach it's empty.
   */
  defaults?: Partial<ReportTemplateAssignmentOverrides>;
  /** Override the submit button label (defaults to "Attach"). */
  submitLabel?: string;
  /**
   * When `true`, the Importance picker is rendered. PromptSet attaches
   * (no per-set importance) leave this false so the dialog stays clean.
   */
  showImportance?: boolean;
  /**
   * AIModel ids (numeric `AIModel.id`) the owning Line of Business
   * supports — from `LineOfBiz.supportedAiModelIds`. When provided, the AI
   * models picker only offers these. `undefined` falls back to the full
   * catalog (e.g. while the LOB is still loading).
   */
  supportedAiModelIds?: number[];
  /**
   * GeoLocation ids the owning Line of Business supports — from
   * `LineOfBiz.supportedGeoLocationIds`. When provided, the geo picker is
   * restricted to these countries. `undefined` falls back to the full
   * catalog.
   */
  supportedGeoLocationIds?: number[];
}

/**
 * Generic dialog for "attach X to a Report Template" flows. Captures
 * Status, Category, Persona, Stage, Brand, Geo, and AI model overrides
 * that apply only to this specific template.
 */
export function ReportTemplateAssignmentDialog({
  open,
  onClose,
  onSave,
  saving,
  error,
  onErrorClose,
  title = 'Attach',
  header,
  defaults,
  submitLabel,
  showImportance = false,
  supportedAiModelIds,
  supportedGeoLocationIds,
}: ReportTemplateAssignmentDialogProps) {
  const [values, setValues] = useState<ReportTemplateAssignmentOverrides>(() => ({
    ...EMPTY_OVERRIDES,
    ...defaults,
  }));

  // Reset whenever the dialog opens — stale state from a previous attach
  // shouldn't leak across opens.
  useEffect(() => {
    if (open) setValues({ ...EMPTY_OVERRIDES, ...defaults });
  }, [open, defaults]);

  const aiModels = useQuery({
    queryKey: ['ai-models', 'select'],
    queryFn: () => resources.aiModels.list({ pageSize: 200, sort: 'aiEngine' }),
  });

  // Catalog filtered to the LOB's supported models (by numeric AIModel.id).
  // `undefined` = unrestricted (LOB not loaded yet) → show the full list.
  const allowedAiModels = useMemo(
    () =>
      supportedAiModelIds === undefined
        ? aiModels.data?.data ?? []
        : (aiModels.data?.data ?? []).filter((m) => supportedAiModelIds.includes(m.id)),
    [aiModels.data, supportedAiModelIds],
  );

  // Resolve each selected id to its `AIModel` row for chip labels.
  // Resolve against the full catalog (not just the allowed set) so an
  // already-saved model still renders even if it later left the LOB's list.
  const aiModelOptions = useMemo(
    () =>
      (values.aiModelIds ?? [])
        .map((id) => (aiModels.data?.data ?? []).find((m) => m.id === id))
        .filter((m): m is AIModel => !!m),
    [aiModels.data, values.aiModelIds],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(values);
  };

  return (
    <FormDialog
      open={open}
      title={title}
      submitLabel={saving ? 'Saving…' : (submitLabel ?? 'Attach')}
      loading={saving}
      onClose={onClose}
      onSubmit={handleSubmit}
      maxWidth="md"
    >
      {header}

      {error && (
        <Alert severity="error" onClose={onErrorClose}>{error}</Alert>
      )}

      <Stack direction="row" spacing={2}>
        <FormControl fullWidth>
          <InputLabel id="rt-assign-status-label">Status</InputLabel>
          <Select
            labelId="rt-assign-status-label"
            label="Status"
            value={values.status}
            renderValue={(v) => <StatusChip value={v as string} />}
            onChange={(e) =>
              setValues({ ...values, status: e.target.value as PromptAssignmentStatus })
            }
          >
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                <StatusChip value={s} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {showImportance && (
          <FormControl fullWidth>
            <InputLabel id="rt-assign-importance-label">Importance</InputLabel>
            <Select
              labelId="rt-assign-importance-label"
              label="Importance"
              value={values.importance}
              renderValue={(v) => <StatusChip value={v as string} />}
              onChange={(e) =>
                setValues({ ...values, importance: e.target.value as PromptImportance })
              }
            >
              {IMPORTANCES.map((i) => (
                <MenuItem key={i} value={i}>
                  <StatusChip value={i} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Stack>

      <Stack direction="row" spacing={2}>
        <EntitySelect<LineOfBizCategory>
          cacheKey={['line-of-biz-categories', 'select']}
          service={resources.lineOfBizCategories}
          label="Category"
          value={values.categoryId}
          onChange={(id) => setValues({ ...values, categoryId: id })}
        />
        <EntitySelect<LineOfBizPersona>
          cacheKey={['line-of-biz-personas', 'select']}
          service={resources.lineOfBizPersonas}
          label="Persona"
          value={values.personaId}
          onChange={(id) => setValues({ ...values, personaId: id })}
        />
      </Stack>

      <Stack direction="row" spacing={2}>
        <EntitySelect<PromptFunnelStage>
          cacheKey={['prompt-funnel-stages', 'select']}
          service={resources.promptFunnelStages}
          label="Stage"
          value={values.stageId}
          onChange={(id) => setValues({ ...values, stageId: id })}
        />
        <TreeEntitySelect<TenantBrand>
          cacheKey={['tenant-brands', 'select', 'all']}
          service={resources.tenantBrands}
          parentIdField="parentBrandId"
          label="Brand"
          value={values.brandId}
          onChange={(id) => setValues({ ...values, brandId: id })}
        />
      </Stack>

      <Stack direction="row" spacing={2}>
        <Autocomplete<AIModel, true, false, false>
          // Multi-select — the prompt/set may be run against several models.
          multiple
          // Dropdown stays open across picks so the user can tick several.
          disableCloseOnSelect
          fullWidth
          // Allowed set + any already-selected rows, so saved-but-now-
          // unsupported models still resolve their chip without a warning.
          options={[
            ...allowedAiModels,
            ...aiModelOptions.filter((m) => !allowedAiModels.some((a) => a.id === m.id)),
          ]}
          value={aiModelOptions}
          onChange={(_, next) =>
            setValues({ ...values, aiModelIds: next.map((m) => m.id) })
          }
          getOptionLabel={(m) => `${m.name} (${m.aiModelIdentifier})`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          loading={aiModels.isLoading}
          renderOption={(props, option, { selected }) => (
            // Checkbox per row signals "this is a multi-select".
            <Box component="li" {...props} key={option.id}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              <Box>
                <Typography variant="body2">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.aiEngine} · {option.aiModelIdentifier}
                </Typography>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="AI models"
              helperText="Pick one or more — the prompt is run against each selected model."
            />
          )}
        />
        <GeoLocationMultiSelect
          value={values.geoLocationIds}
          onChange={(ids) => setValues({ ...values, geoLocationIds: ids })}
          allowedIds={supportedGeoLocationIds}
          helperText="Zero or more countries this assignment targets."
        />
      </Stack>
    </FormDialog>
  );
}
